/**
 * 權限中介層（Middleware）
 * 
 * 用途：
 * 1. 統一管理所有權限邏輯
 * 2. 未來可直接搬移到 Node.js 後端 API
 * 3. 所有 API 呼叫前都必須通過這層檢查
 * 
 * 使用方式：
 * import { checkPermission, filterByPermission } from '../middleware/permissions';
 * 
 * const canEdit = checkPermission('folder.edit', folder, currentUser);
 * const visibleFolders = filterByPermission('folder', allFolders, currentUser);
 */

// ========================================
// 角色判斷工具
// ========================================

export const isSuperAdmin = (profile) => {
  return profile?.role === 'super_admin';
};

export const isDepartmentAdmin = (profile) => {
  return profile?.role === 'department_admin' || profile?.role === 'super_admin';
};

export const isMember = (profile) => {
  return profile?.role === 'member';
};

export const isInSameDepartment = (profile, targetDepartmentId) => {
  if (!profile?.department_id || !targetDepartmentId) return false;
  return profile.department_id === targetDepartmentId;
};

/**
 * 取得有效的部門 ID
 * caller 應直接傳入 profile?.department_id
 * @param {Object} _profile - 使用者 profile（保留參數以維持向下相容）
 * @param {number|null} departmentId - 部門 ID
 * @returns {number|null}
 */
export const getEffectiveDepartmentId = (_profile, departmentId) => {
  return departmentId;
};

/**
 * 取得有效的公司 ID
 * - Super Admin: 使用檢視公司 ID
 * - 其他人: 使用自己的公司 ID
 * @param {Object} profile - 使用者 profile
 * @param {number|null} viewingCompanyId - Super Admin 檢視的公司 ID
 * @returns {number|null}
 */
export const getEffectiveCompanyId = (profile, viewingCompanyId) => {
  if (isSuperAdmin(profile)) {
    return viewingCompanyId; // Super Admin 使用檢視公司
  }
  return profile?.company_id; // 其他人使用實際公司
};

/**
 * 檢查是否在指定部門內
 * @param {Object} profile - 使用者 profile
 * @param {number} targetDepartmentId - 目標部門 ID
 * @param {number|null} departmentId - 部門 ID（用於過濾）
 * @returns {boolean}
 */
export const isInViewingDepartment = (profile, targetDepartmentId, departmentId = null) => {
  const effectiveDeptId = getEffectiveDepartmentId(profile, departmentId);
  if (!effectiveDeptId || !targetDepartmentId) return false;
  return effectiveDeptId === targetDepartmentId;
};

// ========================================
// Profiles 權限
// ========================================

/**
 * 檢查是否可以查看某個使用者
 */
export const canViewProfile = (profile, targetProfile) => {
  // Super Admin 可以看所有人
  if (isSuperAdmin(profile)) return true;
  
  // 可以看自己
  if (profile.id === targetProfile.id) return true;
  
  // 可以看同部門的人
  if (isInSameDepartment(profile, targetProfile.department_id)) return true;
  
  return false;
};

/**
 * 檢查是否可以修改某個使用者
 */
export const canEditProfile = (profile, targetProfile) => {
  // Super Admin 可以編輯所有人
  if (isSuperAdmin(profile)) return true;
  
  // 可以編輯自己
  if (profile.id === targetProfile.id) return true;
  
  // Department Admin 可以編輯本部門的 member
  if (isDepartmentAdmin(profile) && 
      isInSameDepartment(profile, targetProfile.department_id) &&
      targetProfile.role === 'member') {
    return true;
  }
  
  return false;
};

/**
 * 檢查是否可以修改角色
 */
export const canChangeRole = (profile) => {
  // 只有 Super Admin 可以修改角色
  return isSuperAdmin(profile);
};

/**
 * 過濾可見的使用者列表
 */
export const filterVisibleProfiles = (profiles, currentProfile) => {
  if (!currentProfile) return [];
  return profiles.filter(p => canViewProfile(currentProfile, p));
};

// ========================================
// Folders 權限
// ========================================

/**
 * 檢查是否可以查看資料夾（支援繼承父 folder 權限）
 * @param {Object} profile - 使用者 profile
 * @param {Object} folder - folder 物件
 * @param {Object} foldersMap - folder_id -> folder 的 map（可選，用於遞迴查詢父 folder）
 */
export const canViewFolder = (profile, folder, foldersMap = {}) => {
  if (!profile || !folder) return false;
  
  // Super Admin 可以看所有 folder
  if (isSuperAdmin(profile)) {
    return true;
  }
  
  // 是 owner
  if (folder.owner_id === profile.id) {
    return true;
  }
  
  // 是協作者
  if (folder.folder_permissions?.some(p => p.user_id === profile.id)) {
    return true;
  }
  
  // 共享給我的部門（跨部門共享）
  if (folder.shared_with_departments?.includes(profile.department_id)) {
    return true;
  }
  
  // 公開資料夾的所屬部門成員可以看到
  // 邏輯：只要是 is_public=true，資料夾所屬部門的人都能看
  if (folder.is_public && isInSameDepartment(profile, folder.department_id)) {
    const hasSharedDepts = folder.shared_with_departments && folder.shared_with_departments.length > 0;
    return true;
  }
  
  // 🔍 遞迴檢查父 folder 的權限（權限繼承）
  if (folder.parent_id && foldersMap[folder.parent_id]) {
    const parentFolder = foldersMap[folder.parent_id];
    return canViewFolder(profile, parentFolder, foldersMap);
  }
  
  return false;
};

/**
 * 檢查是否可以編輯資料夾
 */
export const canEditFolder = (profile, folder) => {
  if (!profile || !folder) return false;
  
  // Super Admin 可以編輯所有 folder
  if (isSuperAdmin(profile)) return true;
  
  // 是 owner
  if (folder.owner_id === profile.id) return true;
  
  // 是協作者（編輯權限）
  if (folder.folder_permissions?.some(
    p => p.user_id === profile.id && p.role === 'collaborator'
  )) {
    return true;
  }
  
  // Department Admin 可以編輯本部門的公開 folder
  if (isDepartmentAdmin(profile) && 
      folder.is_public && 
      isInSameDepartment(profile, folder.department_id)) {
    return true;
  }
  
  return false;
};

/**
 * 檢查是否可以刪除資料夾
 */
export const canDeleteFolder = (profile, folder) => {
  if (!profile || !folder) return false;
  
  // Super Admin 可以刪除所有 folder
  if (isSuperAdmin(profile)) return true;
  
  // 是 owner
  if (folder.owner_id === profile.id) return true;
  
  return false;
};

/**
 * 檢查是否可以建立資料夾
 */
export const canCreateFolder = (profile) => {
  // 所有已登入用戶都可以建立（但會自動設定 department_id）
  return profile?.id != null;
};

/**
 * 過濾可見的資料夾列表
 * @param {Array} folders - folder 列表
 * @param {Object} currentProfile - 當前使用者 profile
 * @returns {Array} 過濾後的 folder 列表
 */
export const filterVisibleFolders = (folders, currentProfile) => {
  if (!currentProfile) return [];
  
  // 建立 foldersMap 以支援遞迴查詢父 folder
  const foldersMap = {};
  folders.forEach(f => {
    foldersMap[f.id] = f;
  });
  
  return folders.filter(f => canViewFolder(currentProfile, f, foldersMap));
};

// ========================================
// Items 權限（繼承 Folder）
// ========================================

// ========================================
// Status Options 權限
// ========================================

/**
 * 檢查是否可以查看狀態選項
 * @param {Object} profile - 使用者 profile
 * @param {Object} statusOption - 狀態選項
 * @param {number|null} departmentId - 部門 ID（用於過濾）
 */
export const canViewStatusOption = (profile, statusOption, departmentId = null) => {
  if (!profile || !statusOption) return false;

  const effectiveDeptId = getEffectiveDepartmentId(profile, departmentId);
  
  // 系統預設狀態（department_id = null）所有人都可以看
  if (!statusOption.department_id) return true;
  
  // Super Admin：根據檢視部門過濾
  if (isSuperAdmin(profile)) {
    return statusOption.department_id === effectiveDeptId;
  }
  
  // 可以看本部門的
  if (isInSameDepartment(profile, statusOption.department_id)) return true;
  
  return false;
};

/**
 * 檢查是否可以管理狀態選項
 */
export const canManageStatusOptions = (profile) => {
  // 只有 Department Admin 和 Super Admin 可以管理
  return isDepartmentAdmin(profile);
};

/**
 * 過濾可見的狀態選項
 * @param {Array} statusOptions - 狀態選項列表
 * @param {Object} currentProfile - 當前使用者 profile
 * @param {number|null} departmentId - 部門 ID（用於過濾）
 */
export const filterVisibleStatusOptions = (statusOptions, currentProfile, departmentId = null) => {
  if (!currentProfile) return [];
  return statusOptions.filter(s => canViewStatusOption(currentProfile, s, departmentId));
};

// ========================================
// Items 權限
// ========================================

/**
 * 檢查是否可以查看任務
 * 依賴 folder 的權限繼承
 * @param {Object} currentProfile - 當前使用者 profile
 * @param {Object} item - 任務物件
 * @param {Object} folder - folder 物件（可選）
 * @param {Object} foldersMap - folder_id -> folder 的 map（可選，用於遞迴查詢父 folder）
 */
export const canViewItem = (currentProfile, item, folder = null, foldersMap = {}) => {
  if (!currentProfile || !item) return false;
  
  // Super Admin 可以看到所有任務
  if (isSuperAdmin(currentProfile)) return true;
  
  // 如果有傳入 folder，直接使用（並傳入 foldersMap 以支援遞迴）
  if (folder) {
    return canViewFolder(currentProfile, folder, foldersMap);
  }
  
  // 如果沒有 folder 資訊，無法判斷（應該要有 folder_id）
  // 這種情況不應該發生，但為了安全起見：
  // - 如果是建立者，可以看
  // - 如果是被指派者，可以看
  // - 管理員可以看（假設是本部門的任務）
  if (item.creator_id === currentProfile.id || item.assignee_id === currentProfile.id) {
    return true;
  }
  
  if (isDepartmentAdmin(currentProfile)) {
    return true;
  }
  
  return false;
};

/**
 * 檢查是否可以編輯任務
 */
export const canEditItem = (currentProfile, item, folder = null) => {
  if (!currentProfile || !item) return false;
  
  // Super Admin 可以編輯所有任務
  if (isSuperAdmin(currentProfile)) return true;
  
  // 如果有傳入 folder，使用 folder 權限判斷
  if (folder) {
    // 建立者或被指派者可以編輯
    if (item.creator_id === currentProfile.id || item.assignee_id === currentProfile.id) {
      return true;
    }
    // 有 folder 編輯權限也可以
    return canEditFolder(currentProfile, folder);
  }
  
  // 如果沒有 folder 資訊：
  // - 建立者可以編輯
  // - 被指派者可以編輯
  // - 管理員可以編輯
  if (item.creator_id === currentProfile.id || item.assignee_id === currentProfile.id) {
    return true;
  }
  
  if (isDepartmentAdmin(currentProfile)) {
    return true;
  }
  
  return false;
};

/**
 * 檢查是否可以刪除任務
 */
export const canDeleteItem = (currentProfile, item, folder = null) => {
  if (!currentProfile || !item) return false;
  
  // Super Admin 可以刪除所有任務
  if (isSuperAdmin(currentProfile)) return true;
  
  // 建立者可以刪除
  if (item.creator_id === currentProfile.id) return true;
  
  // 如果有傳入 folder，檢查 folder 權限
  if (folder) {
    return canEditFolder(currentProfile, folder);
  }
  
  // 如果沒有 folder 資訊，管理員可以刪除
  if (isDepartmentAdmin(currentProfile)) {
    return true;
  }
  
  return false;
};

/**
 * 過濾可見的任務
 * 注意：Items 沒有 department_id，需要透過 folder 判斷
 * 但為了效能，這裡簡化邏輯：
 * - Super Admin 看所有
 * - 其他人看建立者/被指派者/管理員的任務
 */
/**
 * 過濾可見的任務列表
 * @param {Array} items - 任務列表
 * @param {Object} currentProfile - 當前使用者 profile
 * @param {Object} foldersMap - folder_id -> folder 物件的 map（可選）
 */
export const filterVisibleItems = (items, currentProfile, foldersMap = {}) => {
  if (!currentProfile) return [];
  
  // Super Admin 可以看到所有任務
  if (isSuperAdmin(currentProfile)) return items;
  
  // 其他角色：只顯示可見的任務
  return items.filter(item => {
    // 如果有 folder 資訊，傳入 folder 物件和 foldersMap 以便更準確的權限判斷（支援遞迴）
    const folder = foldersMap[item.folder_id];
    return canViewItem(currentProfile, item, folder, foldersMap);
  });
};

// ========================================
// Departments 權限
// ========================================

/**
 * 檢查是否可以管理部門
 */
export const canManageDepartments = (profile) => {
  // 只有 Super Admin 可以管理部門
  return isSuperAdmin(profile);
};

/**
 * 檢查是否可以編輯部門跑馬燈
 */
export const canEditDepartmentMarquee = (profile, departmentId) => {
  if (!profile) return false;
  
  // Super Admin 可以編輯所有部門
  if (isSuperAdmin(profile)) return true;
  
  // Department Admin 可以編輯自己的部門
  if (isDepartmentAdmin(profile) && isInSameDepartment(profile, departmentId)) {
    return true;
  }
  
  return false;
};

// ========================================
// 統一的權限檢查介面
// ========================================

/**
 * 統一的權限檢查函數
 * @param {string} action - 動作類型（格式：'resource.action'）
 * @param {object} resource - 資源物件
 * @param {object} currentProfile - 當前使用者 profile
 * @returns {boolean}
 */
export const checkPermission = (action, resource, currentProfile) => {
  if (!currentProfile) return false;
  
  const [resourceType, actionType] = action.split('.');
  
  switch (resourceType) {
    case 'profile':
      if (actionType === 'view') return canViewProfile(currentProfile, resource);
      if (actionType === 'edit') return canEditProfile(currentProfile, resource);
      if (actionType === 'changeRole') return canChangeRole(currentProfile);
      break;
      
    case 'folder':
      if (actionType === 'view') return canViewFolder(currentProfile, resource);
      if (actionType === 'edit') return canEditFolder(currentProfile, resource);
      if (actionType === 'delete') return canDeleteFolder(currentProfile, resource);
      if (actionType === 'create') return canCreateFolder(currentProfile);
      break;
      
    case 'item':
      if (actionType === 'view') return canViewItem(currentProfile, resource, resource.folder);
      if (actionType === 'edit') return canEditItem(currentProfile, resource, resource.folder);
      if (actionType === 'delete') return canDeleteItem(currentProfile, resource, resource.folder);
      break;
      
    case 'statusOption':
      if (actionType === 'view') return canViewStatusOption(currentProfile, resource);
      if (actionType === 'manage') return canManageStatusOptions(currentProfile);
      break;
      
    case 'department':
      if (actionType === 'manage') return canManageDepartments(currentProfile);
      if (actionType === 'editMarquee') return canEditDepartmentMarquee(currentProfile, resource.id);
      break;
  }
  
  return false;
};

/**
 * 統一的過濾函數
 * @param {string} resourceType - 資源類型
 * @param {array} resources - 資源列表
 * @param {object} currentProfile - 當前使用者 profile
 * @returns {array}
 */
export const filterByPermission = (resourceType, resources, currentProfile) => {
  if (!currentProfile || !resources) return [];
  
  switch (resourceType) {
    case 'profile':
      return filterVisibleProfiles(resources, currentProfile);
    case 'folder':
      return filterVisibleFolders(resources, currentProfile);
    case 'statusOption':
      return filterVisibleStatusOptions(resources, currentProfile);
    default:
      return resources;
  }
};

